"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var extend_1 = tslib_1.__importDefault(require("extend"));
var loglevel_1 = tslib_1.__importDefault(require("loglevel"));
var NonHtmlAnchorClickTracking_1 = tslib_1.__importDefault(require("./feature/NonHtmlAnchorClickTracking"));
var OutboundFormSubmissionTracking_1 = tslib_1.__importDefault(require("./feature/OutboundFormSubmissionTracking"));
var TrackerUtil_1 = tslib_1.__importDefault(require("./tracker/TrackerUtil"));
var GTag_1 = tslib_1.__importDefault(require("./tracker/GTag"));
var Analytics = /** @class */ (function () {
    function Analytics(options) {
        var _this = this;
        var _a, _b;
        this._logger = null;
        this.gTag = null;
        Analytics.instance = this;
        // Options
        this.options = (0, extend_1.default)(true, {}, Analytics.defaultOptions, options);
        if (this.options.logger.defaultLevelForPlatforms === undefined) {
            this.options.logger.defaultLevelForPlatforms = this.options.logger.defaultLevel;
        }
        options = this.options;
        // Add hostname of the site to the list.
        if (options.hostnames !== undefined) {
            options.hostnames.push(window.location.hostname);
            if (window.location.hostname.toLowerCase().indexOf("www.") === 0) {
                options.hostnames.push(window.location.hostname.substring(4));
            }
            else {
                options.hostnames.push("www." + window.location.hostname);
            }
        }
        // Setup logger
        // We call getLogger() here to ensure it is configured before we start adding trackers which (by default) use the same logger name.
        var logger = this.getLogger();
        // Cause GTag to setup right away
        // If returns null, try again when DOM is loaded (if it is not already; hence the readyState checks)
        var gTag = this.getGTagTracker();
        if (gTag === null
            && document.readyState !== "interactive"
            && document.readyState !== "complete") {
            window.addEventListener("DOMContentLoaded", function () {
                _this.getGTagTracker();
            });
        }
        // Setup features
        if ((_a = this.options.features) === null || _a === void 0 ? void 0 : _a.nonHtmlAnchorClickTracking) {
            new NonHtmlAnchorClickTracking_1.default(this);
        }
        if ((_b = this.options.features) === null || _b === void 0 ? void 0 : _b.outboundFormSubmissionTracking) {
            new OutboundFormSubmissionTracking_1.default(this);
        }
        // Make accessible - For example, window["@plaudit/analytics"] will call Analytics.getInstance().
        if (this.options.globalInstanceKey !== undefined && this.options.globalInstanceKey !== null && this.options.globalInstanceKey.length !== 0) {
            Object.defineProperty(window, this.options.globalInstanceKey, { get: Analytics.getInstance });
        }
        // Log information
        logger.debug("Analytics instance initialized.", "Options: ", this.options);
    }
    Analytics.setup = function (options) {
        if (!Analytics.isSetup()) {
            return Analytics.instance = new Analytics(options);
        }
        throw new Error("Instantiation failed: Cannot reconfigure Analytics. An instance was already created. To obtain a reference call getInstance().");
    };
    Analytics.isSetup = function () {
        return Analytics.instance !== undefined;
    };
    Analytics.getInstance = function () {
        if (!Analytics.isSetup()) {
            throw new Error("You must call Analytics.setup() once prior to calling Analytics.getInstance()");
        }
        return Analytics.instance;
    };
    Analytics.prototype.getGTagTracker = function () {
        if (this.gTag === null) {
            var logger = this.getLogger();
            var gTag = GTag_1.default.getInstance(this.options.gTag);
            if (gTag === null) {
                logger.warn("No instance for \"GTag\" could be found.");
            }
            this.gTag = gTag;
        }
        return this.gTag;
    };
    /**
     * @param {string} pagePath - The path portion of a URL.
     * @param {string=} title - The title of the page (e.g. homepage)
     */
    Analytics.prototype.trackPageview = function (pagePath, title) {
        var _a;
        var isLogging = this.isLoggingAtLeast(loglevel_1.default.levels.INFO);
        if (isLogging)
            console.group("Analytics.trackPageview()");
        // Call `trackPageview()`
        (_a = this.getGTagTracker()) === null || _a === void 0 ? void 0 : _a.trackPageview(pagePath, title);
        // Log
        this.getLogger().info("Analytics.trackPageview:", {
            page: pagePath,
            title: title
        });
        if (isLogging)
            console.groupEnd();
    };
    /**
     * @param {string} category -
     * @param {string|string[]} action -
     * @param {string|number|string[]} label - Optional.
     * @param {number} value - Optional
     * @param {boolean} nonInteraction - Optional
     */
    Analytics.prototype.trackEvent = function (category, action, label, value, nonInteraction) {
        var isLogging = this.isLoggingAtLeast(loglevel_1.default.levels.INFO);
        if (isLogging)
            console.group("Analytics.trackEvent()");
        action = TrackerUtil_1.default.eventActionToString(action);
        label = TrackerUtil_1.default.eventLabelToString(label);
        var gTag = this.getGTagTracker();
        // Call `trackEvent()`
        if (gTag !== null) {
            gTag.trackEvent(category, action, label, value, nonInteraction);
        }
        // Log
        this.getLogger().info("Analytics.trackEvent:", {
            category: category,
            action: action,
            label: label,
            value: value,
            nonInteraction: nonInteraction
        });
        if (isLogging)
            console.groupEnd();
    };
    Analytics.prototype.isLoggingAtLeast = function (level) {
        var logger = this.getLogger();
        return logger.getLevel() <= level;
    };
    Analytics.prototype.getLogger = function () {
        if (this._logger === null) {
            this._logger = loglevel_1.default.getLogger(this.getLoggerName());
            if (this.options.logger !== undefined && this.options.logger.defaultLevel !== undefined && this.options.logger.defaultLevel !== null) {
                this._logger.setDefaultLevel(this.options.logger.defaultLevel);
            }
        }
        return this._logger;
    };
    /**
     * A convenience method to set log level on logger to for this class,
     * and for instance of GTag.
     * Can be used in the browser, by running the following in the console:
     * 		window["@plaudit/analytics"].setLogLevel(0);
     */
    Analytics.prototype.setLogLevel = function (level, persist) {
        if (persist === void 0) { persist = true; }
        this.getLogger().setLevel(level, persist);
        var gTag = this.getGTagTracker();
        if (gTag !== null) {
            gTag.getLogger().setLevel(level, persist);
        }
    };
    /**
     * Get the name of the logger for this instance
     */
    Analytics.prototype.getLoggerName = function (platformName) {
        var loggerName = this.options.logger.name;
        if (platformName !== undefined && platformName !== null) {
            loggerName += "-" + platformName;
        }
        return loggerName;
    };
    /**
     * Get the default logging level for a platform. This is set through the options
     * object passed to Analytics.getInstance()
     */
    Analytics.prototype.getLoggerDefaultLevel = function (platformName) {
        var defaultLevel;
        if (platformName !== undefined && platformName !== null) {
            defaultLevel = this.options.logger.defaultLevelForPlatforms;
        }
        else {
            defaultLevel = this.options.logger.defaultLevel;
        }
        return defaultLevel;
    };
    Analytics.prototype.getHostnames = function () {
        return this.options.hostnames;
    };
    Analytics.defaultOptions = {
        globalInstanceKey: "@plaudit/analytics",
        features: {
            nonHtmlAnchorClickTracking: true,
            outboundFormSubmissionTracking: true
        },
        hostnames: [],
        logger: {
            name: "@plaudit/analytics",
            defaultLevel: loglevel_1.default.levels.SILENT,
            // defaultLevelForPlatforms is set to the same as defaultLevel unless set separtely
        },
        gTag: {
            trackPageviewTriggers: {
                onPageLoaded: true,
                onHistoryStateChange: true
            }
        }
    };
    Analytics.emptyValue = "(not set)"; // Standard value to use when a dimension is not set / empty.
    Analytics.valueDelimiter = " • "; // Standard value to use as a delimiter. For example, when joining an array to create a string to report as a label: label.join(Analytics.valueDelimiter)
    return Analytics;
}());
exports.default = Analytics;
